import { useMemo } from "react"

import { Table, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import EmptyComponent from "../../../../Components/Empty"
import { STATUS_GROUP_META } from "../../../ReceiptPage/constants/receipt-status-mapping"
import HorizontalBars from "../../charts/HorizontalBars"
import { SERIES_COLORS } from "../../charts/palette"
import GroupMarker from "../../components/cells/GroupMarker"
import { numberCell } from "../../components/cells/numberCell"
import KpiStrip from "../../components/KpiStrip/KpiStrip"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { useCardData } from "../../hooks/useCardData"
import { formatCount } from "../../utils/formats"

import rowLines from "../../components/RowLines/rowLines.module.less"

const { Text } = Typography

// The card's Tooltip terms, verbatim from the spec (§7 Records by status).
const INFO = [
  {
    name: "Records by group",
    formula:
      "Records per status group (Active, Pending, Needs help, Unenrolled, Failed), separately for CC and OI"
  },
  { name: "Records by status", formula: "The same per detailed status" },
  {
    name: "Merchants by program status",
    formula:
      "Best group among the merchant's records in the program: Active → Pending → Needs help → Unenrolled → Failed. CC stays Pending until the Ethoca account is live or locked for edit"
  },
  { name: "Active share", formula: "Records in Active ÷ all records × 100" }
]

// The status groups in the spec's order and words (DAA-7-02); their colours are the platform's
// (STATUS_GROUP_META).
const GROUPS = [
  { id: "active", name: "Active" },
  { id: "pending", name: "Pending" },
  { id: "needs_help", name: "Needs help" },
  { id: "unenrolled", name: "Unenrolled" },
  { id: "failed", name: "Failed" }
]

const GROUP_NAMES = Object.fromEntries(GROUPS.map((group) => [group.id, group.name]))

const PROGRAMS = ["cc", "oi"]

// Records by status (DAA-7-01 … DAA-7-12): where descriptors stand right now in each program, and
// how many merchants are in each program status — a snapshot, so the dates, Compare and Channels do
// not reach it (DAA-7-05). The answer holds only the programs the Program filter keeps.
const RecordsByStatusCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("recordsByStatus")
  const kpis = data?.kpis
  // The programs of the answer; both until the first one arrives.
  const programs = useMemo(() => (kpis ? Object.keys(kpis.active_share) : PROGRAMS), [kpis])

  const tiles = useMemo(() => {
    // With one program selected the other program's tile is hidden (DAA-7-06).
    const shareTiles = programs.map((program) => ({
      key: `activeShare-${program}`,
      label: `${t("Active share")} · ${program.toUpperCase()}`,
      value: kpis?.active_share[program],
      decimals: 1,
      suffix: "%",
      secondary: kpis ? `${formatCount(kpis.active_records[program])} ${t("active records")}` : null
    }))

    return [
      ...shareTiles,
      {
        key: "records",
        label: t("Records"),
        value: kpis?.records,
        secondary: kpis ? `${formatCount(kpis.records_in_active)} ${t("in Active")}` : null
      },
      {
        key: "merchantsActive",
        label: t("Merchants Active"),
        value: kpis?.merchants_active,
        secondary: t("program status Active")
      }
    ]
  }, [kpis, programs, t])

  // One bar per group, labelled with each program's count (DAA-7-10); no records reads "No data
  // available" (DAA-1-18).
  const groupRows = useMemo(() => {
    if (!kpis?.records) return []

    return GROUPS.map((group) => {
      const counts = data.by_group[group.id]

      return {
        key: group.id,
        label: t(group.name),
        color: STATUS_GROUP_META[group.id].hex,
        value: Object.values(counts).reduce((sum, count) => sum + count, 0),
        text: Object.entries(counts)
          .map(([program, count]) => `${program.toUpperCase()} ${formatCount(count)}`)
          .join(" · ")
      }
    })
  }, [data, kpis, t])

  // A CC and an OI bar per group, the group named once (DAA-7-12).
  const merchantRows = useMemo(() => {
    const merchantsByProgram = data?.merchants_by_program_status

    if (!merchantsByProgram) return []

    const rows = GROUPS.flatMap((group) =>
      programs.map((program, index) => {
        const count = merchantsByProgram[program][group.id]

        return {
          key: `${group.id}-${program}`,
          label: index === 0 ? t(group.name) : "",
          color: SERIES_COLORS[program],
          value: count,
          text: `${program.toUpperCase()} ${formatCount(count)}`
        }
      })
    )

    // No merchant in any program status: the panel reads "No data available" (DAA-1-18).
    return rows.some((row) => row.value) ? rows : []
  }, [data, programs, t])

  // The design's column widths (2 : 1.2 : 1 : 1); a long stored status is cut with an ellipsis, never
  // wrapped.
  const columns = useMemo(() => {
    const countCell = numberCell(formatCount)
    // A program the filter leaves out reads "—" (DAA-7-11).
    const programColumn = (program) => ({
      title: program.toUpperCase(),
      dataIndex: program,
      width: "19.23%",
      align: "right",
      render: (count) => (count == null ? <Text type="secondary">—</Text> : countCell(count))
    })

    return [
      {
        title: t("Status"),
        dataIndex: "status",
        width: "38.46%",
        render: (status) => <Text ellipsis={{ tooltip: status }}>{status}</Text>
      },
      {
        title: t("Group"),
        dataIndex: "group",
        width: "23.08%",
        render: (group) => (
          <GroupMarker color={STATUS_GROUP_META[group].hex} name={t(GROUP_NAMES[group])} />
        )
      },
      ...PROGRAMS.map(programColumn)
    ]
  }, [t])

  // Beside four KPIs the two panels split under the middle divider, beside three under the first
  // one (DAA-1-21).
  const leftSpan = tiles.length === 4 ? 6 : 4

  return (
    <MetricCard
      title={t("Records by status")}
      info={INFO}
      notAffectedBy={t("Snapshot now · Not affected by: Channels, Date range, Compare")}
      loading={loading}
    >
      <KpiStrip tiles={tiles} />
      {/* The panels keep their place until the first answer, so the page does not jump under them. */}
      <PanelRow>
        <Panel title={t("Records by group")} span={leftSpan}>
          {/* A block, so an empty state stays under the title rather than centred beside the long
              table. */}
          <div>{data && <HorizontalBars rows={groupRows} />}</div>
        </Panel>
        <Panel title={t("Detailed statuses")} span={12 - leftSpan}>
          {data && (
            <Table
              className={cls("chart-table", rowLines.rowLines)}
              rowKey="status"
              dataSource={data.by_status}
              columns={columns}
              tableLayout="fixed"
              pagination={false}
              // Narrower than 520px the table scrolls sideways instead of cutting the names, as the
              // design's does.
              scroll={{ x: 520 }}
              locale={{ emptyText: <EmptyComponent type="no_data_available" /> }}
            />
          )}
        </Panel>
      </PanelRow>
      <PanelRow>
        <Panel
          title={t("Merchants by program status")}
          tag={t("best group among the merchant’s records in the program")}
        >
          {data && <HorizontalBars rows={merchantRows} />}
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default RecordsByStatusCard
